import type { ApplicationJourneyHeaderDto } from '../api/admin';
import type { IdentityLookupRow } from '../api/identityVerificationDashboardApi';

export type OverviewPersonalDetails = {
  fullName: string | null;
  dateOfBirth: string | null;
  address: string | null;
};

function asRecord(v: unknown): Record<string, unknown> | null {
  if (!v || typeof v !== 'object' || Array.isArray(v)) return null;
  return v as Record<string, unknown>;
}

function pickString(...vals: unknown[]): string | null {
  for (const v of vals) {
    if (typeof v === 'string' && v.trim()) return v.trim();
  }
  return null;
}

function extractedMap(row: IdentityLookupRow | null | undefined): Map<string, string> {
  const map = new Map<string, string>();
  for (const field of row?.dossier?.extractedSummary ?? []) {
    const key = String(field.key ?? '').trim().toLowerCase();
    const value = String(field.value ?? '').trim();
    if (key && value) map.set(key, value);
  }
  return map;
}

function fromExtracted(map: Map<string, string>, keys: string[]): string | null {
  for (const key of keys) {
    const value = map.get(key.toLowerCase());
    if (value) return value;
  }
  return null;
}

/** Build a single address line from flat OCR keys (Mitek / AI OCR shape). */
export function formatAddressFromOcr(ocr: Record<string, unknown> | null | undefined): string | null {
  if (!ocr) return null;
  const nested = asRecord(ocr.address) ?? {};
  const src: Record<string, unknown> = { ...nested, ...ocr };

  const full = pickString(
    src.formattedAddress,
    src.formatted_address,
    src.fullAddress,
    src.full_address,
    src.residential_address,
    src.addressString,
  );
  if (full) return full;

  const line1 = pickString(
    src.address_line1,
    src.addressLine1,
    src.AddressLine1,
    src.street,
    src.street_address,
    src.thoroughfare,
  );
  const line2 = pickString(src.address_line2, src.addressLine2, src.AddressLine2, src.unit);
  if (line1 || line2) {
    const cityLine = pickString(src.city, src.locality, src.municipality, src.town);
    const province = pickString(src.province, src.state, src.administrativeArea, src.region);
    const postal = pickString(src.postal_code, src.postalCode, src.zip, src.zipCode);
    // Prefer street + line2 when line2 already has city/province; else assemble parts.
    if (line2 && /[A-Za-z].*\d|[A-Z]{2}\s+\w/.test(line2)) {
      return [line1, line2].filter(Boolean).join(', ');
    }
    return [line1, line2, cityLine, province, postal].filter(Boolean).join(', ') || null;
  }

  const parts = [
    pickString(src.city, src.locality),
    pickString(src.province, src.state),
    pickString(src.postal_code, src.postalCode),
    pickString(src.country, src.countryName),
  ].filter(Boolean);
  return parts.length > 0 ? parts.join(', ') : null;
}

function formatDeAddress(
  de: ApplicationJourneyHeaderDto['decisionEngine'] | null | undefined,
): string | null {
  const addr = de?.currentAddress;
  if (!addr) return null;
  const line = [addr.street, addr.city, addr.province, addr.postal].filter(Boolean).join(', ').trim();
  return line || null;
}

/**
 * Resolve Overview personal fields from customer → ID OCR → Mitek summary → Decision Engine.
 * Identity often finishes with OCR populated before a full customer profile row exists.
 */
export function resolveOverviewPersonalDetails(
  application: ApplicationJourneyHeaderDto,
  identityRow: IdentityLookupRow | null | undefined,
): OverviewPersonalDetails {
  const customer = identityRow?.customer ?? null;
  const ocr = asRecord(identityRow?.ocr_data) ?? {};
  const extracted = extractedMap(identityRow);
  const de = application.decisionEngine;

  const fullName =
    pickString(
      [customer?.first_name, customer?.last_name].filter(Boolean).join(' '),
      [ocr.first_name, ocr.last_name].filter(Boolean).join(' '),
      ocr.full_name,
      ocr.fullName,
      fromExtracted(extracted, ['full_name', 'fullName', 'name', 'Full Name']),
      [
        fromExtracted(extracted, ['first_name', 'firstName', 'givenNames', 'Given Names']),
        fromExtracted(extracted, ['last_name', 'lastName', 'surname', 'Surname']),
      ]
        .filter(Boolean)
        .join(' '),
      [de?.applicantFirstName, de?.applicantLastName].filter(Boolean).join(' '),
    ) || null;

  const dateOfBirth =
    pickString(
      customer?.date_of_birth,
      ocr.date_of_birth,
      ocr.dateOfBirth,
      fromExtracted(extracted, ['date_of_birth', 'dateOfBirth', 'Date of Birth', 'dob', 'DOB']),
    ) || null;

  const address =
    pickString(
      customer?.residential_address,
      formatAddressFromOcr(ocr),
      fromExtracted(extracted, [
        'residential_address',
        'formattedAddress',
        'formatted_address',
        'full_address',
        'address',
      ]),
      formatDeAddress(de),
    ) || null;

  return { fullName, dateOfBirth, address };
}
